import {
  getProperty,
  getPropertyAnytimeRate,
  getPropertyCalendarPrices,
  getPropertyRates,
} from 'api/accommodation'
import { MAX_ACCOMMODATION_CALENDAR_MONTHS } from 'constants/calendar'
import { beginningOfToday, formatIsoDate } from 'lib/datetime/dateUtils'
import { filterObject } from 'lib/object/objectUtils'
import buildPropertyCalendarPricesKey from 'lib/offer/buildPropertyCalendarPricesKey'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import getPropertyRatesReduxKeys from 'lib/offer/getPropertyRatesReduxKeys'
import rekeyPropertyRatesBySlot from 'lib/offer/rekeyPropertyRatesBySlot'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import { getSource, isPropertyCollectEnabled } from 'selectors/offerSelectors'

import { API_CALL, PROPERTY_UPDATE_PRICING_DATA } from './actionConstants'
import {
  FETCH_PROPERTY,
  FETCH_PROPERTY_ANYTIME_RATE,
  FETCH_PROPERTY_CALENDAR_PRICES,
  FETCH_PROPERTY_RATES,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'

export function fetchProperty(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.properties[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY,
      request: () => getProperty(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
      }),
      propertyId,
    })
  }
}

export function fetchPropertyAnytimeRate(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.propertyAnytimeRates[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_ANYTIME_RATE,
      request: () => getPropertyAnytimeRate(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
      }),
      propertyId,
    })
  }
}

interface PropertyCalendarPricesParams {
  /** Length of stay, in nights */
  duration: number
  /** Room occupancies */
  occupancies: Array<App.Occupants>
}

export function fetchPropertyCalendarPrices(propertyId: string, {
  duration,
  occupancies,
}: PropertyCalendarPricesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyCalendarPricesKey({ propertyId, duration, occupancies })
    if (!key || state.offer.propertyCalendarPrices[key]) return
    const property = state.offer.properties[propertyId]?.property
    if (!property) return
    const promotion = property.promotion
    if (promotion?.type !== 'dynamicFlash') return
    if (duration < promotion.minimumLos) return
    const earliestIsoDate = formatIsoDate(beginningOfToday(property.location.timezone))
    // NB: When computing `latestIsoDate`, we're not using `addMonths`/`endOfMonth`/`formatIsoDate`
    // because they deal in the user's local time zone, which is impractical to test with our
    // current Jest setup (as we can't mock the system's time zone, which is fixed to UTC).
    //
    // Ideally, we'd be dealing with a naive (i.e. without time zone) date, but failing that, we're
    // sticking to UTC, which is free from DST-related quirks.
    //
    // First, we'll construct `latestDate` from `earliestIsoDate`. The time component is guaranteed
    // to be midnight UTC.
    const latestDate = new Date(earliestIsoDate)
    // Next, reset to the start of the month, so we don't potentially overshoot if `earliestIsoDate`
    // is near the end of the month.
    latestDate.setUTCDate(1)
    // Now add `MAX_ACCOMMODATION_CALENDAR_MONTHS` full months. Note that we've overshot by a month.
    latestDate.setUTCMonth(latestDate.getUTCMonth() + MAX_ACCOMMODATION_CALENDAR_MONTHS)
    // Now roll back to the end of the previous (i.e. correct) month.
    latestDate.setUTCMilliseconds(-1)
    const latestIsoDate = latestDate.toISOString().substring(0, 10)
    const startIsoDate = promotion.travelFromIsoDate > earliestIsoDate ? promotion.travelFromIsoDate : earliestIsoDate
    // NB: `promotion.travelToIsoDate` is the latest that we can check out, not the latest that we
    // can check in.
    const latestTravelFromDate = new Date(promotion.travelToIsoDate)
    latestTravelFromDate.setUTCDate(latestTravelFromDate.getUTCDate() - duration)
    const latestTravelFromIsoDate = latestTravelFromDate.toISOString().substring(0, 10)
    const endIsoDate = latestTravelFromIsoDate < latestIsoDate ? latestTravelFromIsoDate : latestIsoDate
    if (startIsoDate > endIsoDate) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_CALENDAR_PRICES,
      request: () => getPropertyCalendarPrices(propertyId, {
        startIsoYearMonth: startIsoDate.substring(0, 7),
        endIsoYearMonth: endIsoDate.substring(0, 7),
        duration,
        occupancies,
        currency: state.geo.currentCurrency,
      }).then(days => filterObject(days, (key) => startIsoDate <= key && key <= endIsoDate)),
      key,
    })
  }
}

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomRateGroupId?: string
  refresh?: boolean
}

export function fetchPropertyRates(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomRateGroupId,
  refresh = false,
}: PropertyRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const source = getSource(state)

    const reduxKeys = getPropertyRatesReduxKeys({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!reduxKeys) return
    // NB: For better or worse, we have no TTL on cached responses, so rates _will_ become stale and
    // errors _won't_ clear until a hard refresh.
    const expectedKeys = [reduxKeys.combined, ...Object.values(reduxKeys.byOccupancy)]
    const allCached = !refresh && expectedKeys.every(key => !!state.offer.propertyRates[key]?.rooms)
    if (allCached) return

    const reduxKeyBySlot: Record<string, string> = { combined: reduxKeys.combined, ...reduxKeys.byOccupancy }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_RATES,
      request: () => getPropertyRates(propertyId, {
        checkIn,
        checkOut,
        occupancies,
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        source,
        dynamicAvailability: true,
        enablePropertyCollect: isPropertyCollectEnabled(state),
        includeBundles: true,
        utmContent,
        roomTypeId,
        roomRateId,
        roomRateGroupId,
        areFlightsEnabled: state.offer.properties[propertyId]?.property?.areFlightsEnabled ?? false,
        markupSource: getChannelMarkupSource(state),
      }).then(slotMap => rekeyPropertyRatesBySlot(slotMap, reduxKeyBySlot)),
      keys: reduxKeyBySlot,
    })
  }
}

interface PropertyPricingDataParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomId: string
  rateId: string
  /** The new un-marked hotel price; see `PROPERTY_UPDATE_PRICING_DATA`. */
  newBasePrice: number
  newLuxPlusBasePrice: number | undefined
  newPropertyFees: number
}

export function updatePropertyPricingData(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomId,
  rateId,
  newBasePrice,
  newLuxPlusBasePrice,
  newPropertyFees,
}: PropertyPricingDataParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!key || !state.offer.propertyRates[key]) return

    dispatch({
      type: PROPERTY_UPDATE_PRICING_DATA,
      key,
      roomId,
      rateId,
      newBasePrice,
      newLuxPlusBasePrice,
      newPropertyFees,
    })
  }
}
